import * as yup from 'yup';
import { startOfDay } from 'date-fns';
import "yup-phone-lite";
import EventsFormModel from '../../../app/models/events/eventsFormModel';
import { EMAIL_EXPRESSION, MAX_FILE_SIZE, SKIP_TO_UPLOAD } from '../../../app/utils/constant';
import { PcsActivityTypeEnum } from '../../../app/enums/pcsActivityTypeEnum';
import { CountryCode } from 'libphonenumber-js/types';
import { countryCodes, getFileExtension } from '../../../app/utils/util';
import { EventSponsorshipEnum } from './eventSponsorshipEnum';
import { YesNoEnum } from '../../../app/enums/yesNoEnum';
import { OrganisingInstitutionTypeEnum } from '../../../app/enums/organisingInstitutionTypeEnum';
import { LearningMethodEnum } from '../../../app/enums/learningMethodEnum';
import { imcNumberValidationSchema } from '../../../app/models/pcsEnrolment/registrationValidationSchema';
import { EventOrganiserTypeEnum } from '../../../app/enums/eventOrganiserTypeEnum';

const {
    personalDetailsFormField: { Title, FirstName, Surname, Email, MobileNumber, WorkplaceAddress },
    eventDetailsFormField: {
        AcademicDiscipline, TitleOfActivity, TypeOfActivity, OtherTypeOfActivity,
        ExpectedNumberOfParticipants, IntendedAudience, EventStartDate,
        EventCountry, EventCounty, EventVenue, TypeOfActivityCode
    },
    organiserDetailsFormField: {
        OrganiserType, OrganisingInstitution, OrganisingInstitutionType, OrganisingInstitutionOtherType,
        LeadOrganiserFirstName, LeadOrganiserSurname, LeadOrganiserIMCNumber, LeadOrganiserMobile, LeadOrganiserEmail,
        LeadOrganiserWorkplaceAddress, AreYouEventOrganiser, AdministrationOrganiserDetailsFirstName,
        AdministrationOrganiserSurname, AdministrationOrganiserMobile, AdministrationOrganiserEmail, AdministrationOrganiserWorkplaceAddress
    },
    educationalAimsAndObjectivesDetailsFormField: {
        LearningObjectivesOfActivity, SkillsKnowledgeAcquireDuringTheActivity, LearningTeachingMethods,
        OtherLearningTeachingMethods, EventEvaluation, MedicalCouncils8DomainsOfGoodProfessionalPractice
    },
    sponsorshipFormField: {
        EventSponsored, SponsorshipType, RelationshipBetweenSponsorsAndTheSpeakers,
        RegistrationFeeForAttendees, AmountForRegistrationFeeForAttendees },
    documentFormField: { SupportingDocument },
    calendarOfActivitiesFormField: {
        EventPublishedOnRcsiCalendarOfActivities, ApprovalToAnotherPostgraduateMedicalTrainingBody,
        EventWebsiteUrl, EmailAddress
    }
} = EventsFormModel;

export const ApplicantDetailsValidationSchemas = yup.object().shape({
    [Title.name]: yup.string().required(Title.requiredErrorMsg),
    [FirstName.name]: yup.string().required(FirstName.requiredErrorMsg)
        .max(FirstName.maxStringCount, FirstName.maxStringLengthErrorMsg),
    [Surname.name]: yup.string().required(Surname.requiredErrorMsg)
        .max(Surname.maxStringCount, Surname.maxStringLengthErrorMsg),
    [Email.name]: yup.string().required(`${Email.requiredErrorMsg}`)
        .matches(EMAIL_EXPRESSION, `${Email.typeError}`)
        .max(Email.maxStringCount, Email.maxStringLengthErrorMsg),
    [MobileNumber.name]: yup.string().phone(countryCodes as CountryCode[], `${MobileNumber.typeError}`)
        .required(`${MobileNumber.requiredErrorMsg}`)
        .max(MobileNumber.maxStringCount, MobileNumber.maxStringLengthErrorMsg),
    [WorkplaceAddress.name]: yup.string().required(WorkplaceAddress.requiredErrorMsg)
        .max(WorkplaceAddress.maxStringCount, WorkplaceAddress.maxStringLengthErrorMsg)
});

export const EventDetailsValidationSchema = yup.object().shape({
    [AcademicDiscipline.name]: yup.string().required(AcademicDiscipline.requiredErrorMsg),
    [TitleOfActivity.name]: yup.string().required(TitleOfActivity.requiredErrorMsg)
        .max(TitleOfActivity.maxStringCount, TitleOfActivity.maxStringLengthErrorMsg),
    [TypeOfActivity.name]: yup.string().required(TypeOfActivity.requiredErrorMsg),
    [OtherTypeOfActivity.name]: yup.string().max(OtherTypeOfActivity.maxStringCount, OtherTypeOfActivity.maxStringLengthErrorMsg)
        .when(TypeOfActivityCode.name, {
            is: PcsActivityTypeEnum.Other,
            then: s => s.trim().required(OtherTypeOfActivity.requiredErrorMsg),
            otherwise: s => s.notRequired()
        }),
    [ExpectedNumberOfParticipants.name]: yup.string().required(ExpectedNumberOfParticipants.requiredErrorMsg),
    [IntendedAudience.name]: yup.string().required(IntendedAudience.requiredErrorMsg)
        .max(IntendedAudience.maxStringCount, IntendedAudience.maxStringLengthErrorMsg),
    [EventStartDate.name]: yup.date()
        .transform((value, originalValue) => originalValue === '' ? null : value)
        .nullable().typeError(EventStartDate.typeError)
        .required(EventStartDate.requiredErrorMsg)
        .test('minimum-event-start-date', 'Start date must be greater than or equal to the current date.',
            value => !value || startOfDay(value) >= startOfDay(new Date())),
    [EventCountry.name]: yup.string()
        .when([TypeOfActivityCode.name], ([typeOfActivityCode], schema) => {
            if (typeOfActivityCode === PcsActivityTypeEnum.ELearningCourse ||
                typeOfActivityCode === PcsActivityTypeEnum.Webinar) {
                return schema.notRequired();
            }

            return schema.required(EventCountry.requiredErrorMsg);
        }),
    [EventCounty.name]: yup.string()
        .when([TypeOfActivityCode.name], ([typeOfActivityCode], schema) => {
            if (typeOfActivityCode === PcsActivityTypeEnum.ELearningCourse ||
                typeOfActivityCode === PcsActivityTypeEnum.Webinar) {
                return schema.notRequired();
            }

            return schema.required(EventCounty.requiredErrorMsg);
        }),
    [EventVenue.name]: yup.string().max(EventVenue.maxStringCount, EventVenue.maxStringLengthErrorMsg)
        .when([TypeOfActivityCode.name], ([typeOfActivityCode], schema) => {
            if (typeOfActivityCode === PcsActivityTypeEnum.ELearningCourse ||
                typeOfActivityCode === PcsActivityTypeEnum.Webinar) {
                return schema.notRequired();
            }

            return schema.required(EventVenue.requiredErrorMsg);
        })
});

export const OrganiserDetailsValidationSchemas = yup.object().shape({
    [OrganiserType.name]: yup.string().required(OrganiserType.requiredErrorMsg),
    [OrganisingInstitution.name]: yup.string().required(OrganisingInstitution.requiredErrorMsg)
        .max(OrganisingInstitution.maxStringCount, OrganisingInstitution.maxStringLengthErrorMsg),
    [OrganisingInstitutionType.name]: yup.string().required(OrganisingInstitutionType.requiredErrorMsg),
    [OrganisingInstitutionOtherType.name]: yup.string().max(OrganisingInstitutionOtherType.maxStringCount, OrganisingInstitutionOtherType.maxStringLengthErrorMsg)
        .when(OrganisingInstitutionType.name, {
            is: OrganisingInstitutionTypeEnum.Other,
            then: s => s.trim().required(OrganisingInstitutionOtherType.requiredErrorMsg),
            otherwise: s => s.notRequired()
        }),
    [LeadOrganiserFirstName.name]: yup.string().max(LeadOrganiserFirstName.maxStringCount, LeadOrganiserFirstName.maxStringLengthErrorMsg)
        .required(LeadOrganiserFirstName.requiredErrorMsg),
    [LeadOrganiserSurname.name]: yup.string().max(LeadOrganiserSurname.maxStringCount, LeadOrganiserSurname.maxStringLengthErrorMsg)
        .required(LeadOrganiserSurname.requiredErrorMsg),
    [LeadOrganiserIMCNumber.name]: yup.string().when(OrganiserType.name, {
        is: EventOrganiserTypeEnum.SurgicalMedicalLeadOrganiser,
        then: () => imcNumberValidationSchema,
        otherwise: schema => schema.notRequired()
    }),
    [LeadOrganiserMobile.name]: yup.string().phone(countryCodes as CountryCode[], LeadOrganiserMobile.typeError)
        .max(LeadOrganiserMobile.maxStringCount, LeadOrganiserMobile.maxStringLengthErrorMsg).required(LeadOrganiserMobile.requiredErrorMsg),
    [LeadOrganiserEmail.name]: yup.string().required(LeadOrganiserEmail.requiredErrorMsg)
        .max(LeadOrganiserEmail.maxStringCount, LeadOrganiserEmail.maxStringLengthErrorMsg).matches(EMAIL_EXPRESSION, LeadOrganiserEmail.typeError),
    [LeadOrganiserWorkplaceAddress.name]: yup.string().required(LeadOrganiserWorkplaceAddress.requiredErrorMsg)
        .max(LeadOrganiserWorkplaceAddress.maxStringCount, LeadOrganiserWorkplaceAddress.maxStringLengthErrorMsg),
    [AdministrationOrganiserDetailsFirstName.name]: yup.string().max(AdministrationOrganiserDetailsFirstName.maxStringCount, AdministrationOrganiserDetailsFirstName.maxStringLengthErrorMsg)
        .required(AdministrationOrganiserDetailsFirstName.requiredErrorMsg),
    [AdministrationOrganiserSurname.name]: yup.string().required(AdministrationOrganiserSurname.requiredErrorMsg)
        .max(AdministrationOrganiserSurname.maxStringCount, AdministrationOrganiserSurname.maxStringLengthErrorMsg),
    [AdministrationOrganiserMobile.name]: yup.string().phone(countryCodes as CountryCode[], AdministrationOrganiserMobile.typeError)
        .required(AdministrationOrganiserMobile.requiredErrorMsg).max(AdministrationOrganiserMobile.maxStringCount, AdministrationOrganiserMobile.maxStringLengthErrorMsg),
    [AdministrationOrganiserEmail.name]: yup.string().required(AdministrationOrganiserEmail.requiredErrorMsg)
        .matches(EMAIL_EXPRESSION, AdministrationOrganiserEmail.typeError).max(AdministrationOrganiserEmail.maxStringCount, AdministrationOrganiserEmail.maxStringLengthErrorMsg),
    [AdministrationOrganiserWorkplaceAddress.name]: yup.string()
        .when(AreYouEventOrganiser.name, {
            is: YesNoEnum.NO,
            then: s => s.trim()
                .required(AdministrationOrganiserWorkplaceAddress.requiredErrorMsg)
                .max(AdministrationOrganiserWorkplaceAddress.maxStringCount, AdministrationOrganiserWorkplaceAddress.maxStringLengthErrorMsg),
            otherwise: s => s.notRequired()
        })
});

export const EducationalAimsAndObjectivesValidationSchemas = yup.object().shape({
    [LearningObjectivesOfActivity.name]: yup.string().required(LearningObjectivesOfActivity.requiredErrorMsg)
        .max(LearningObjectivesOfActivity.maxStringCount, LearningObjectivesOfActivity.maxStringLengthErrorMsg),
    [SkillsKnowledgeAcquireDuringTheActivity.name]: yup.string().required(SkillsKnowledgeAcquireDuringTheActivity.requiredErrorMsg)
        .max(SkillsKnowledgeAcquireDuringTheActivity.maxStringCount, SkillsKnowledgeAcquireDuringTheActivity.maxStringLengthErrorMsg),
    [LearningTeachingMethods.name]: yup.array().min(1, LearningTeachingMethods.requiredErrorMsg)
        .of(yup.string().required(LearningTeachingMethods.requiredErrorMsg))
        .required(LearningTeachingMethods.requiredErrorMsg),
    [OtherLearningTeachingMethods.name]: yup.string().when(LearningTeachingMethods.name, {
        is: (methods: string[]) => Array.isArray(methods) && methods.includes(LearningMethodEnum.Other),
        then: (schema) => schema.required(OtherLearningTeachingMethods.requiredErrorMsg),
        otherwise: (schema) => schema.notRequired(),
    }),
    [EventEvaluation.name]: yup.string().required(EventEvaluation.requiredErrorMsg)
        .max(EventEvaluation.maxStringCount, EventEvaluation.maxStringLengthErrorMsg),
    [MedicalCouncils8DomainsOfGoodProfessionalPractice.name]: yup.array().min(1, MedicalCouncils8DomainsOfGoodProfessionalPractice.requiredErrorMsg)
        .of(yup.string().required(MedicalCouncils8DomainsOfGoodProfessionalPractice.requiredErrorMsg))
        .required(MedicalCouncils8DomainsOfGoodProfessionalPractice.requiredErrorMsg)
});

export const SponsorshipValidationSchemas = yup.object().shape({
    [EventSponsored.name]: yup.string().required(EventSponsored.requiredErrorMsg),
    [SponsorshipType.name]: yup.string().when(EventSponsored.name, {
        is: EventSponsorshipEnum.EventNotSponsored,
        then: schema => schema.notRequired(),
        otherwise: schema => schema.required(SponsorshipType.requiredErrorMsg)
            .max(SponsorshipType.maxStringCount, SponsorshipType.maxStringLengthErrorMsg)
    }),
    [RelationshipBetweenSponsorsAndTheSpeakers.name]: yup.string().when(EventSponsored.name, {
        is: EventSponsorshipEnum.EventNotSponsored,
        then: schema => schema.notRequired(),
        otherwise: schema => schema.required(RelationshipBetweenSponsorsAndTheSpeakers.requiredErrorMsg)
            .max(RelationshipBetweenSponsorsAndTheSpeakers.maxStringCount, RelationshipBetweenSponsorsAndTheSpeakers.maxStringLengthErrorMsg)
    }),
    [RegistrationFeeForAttendees.name]: yup.string().required(RegistrationFeeForAttendees.requiredErrorMsg),
    [AmountForRegistrationFeeForAttendees.name]: yup.string().trim()
        .when(RegistrationFeeForAttendees.name, {
            is: (val: string) => val.toUpperCase() === YesNoEnum.YES,
            then: (schema) =>
                schema
                    .required(AmountForRegistrationFeeForAttendees.requiredErrorMsg)
                    .max(AmountForRegistrationFeeForAttendees.maxStringCount, AmountForRegistrationFeeForAttendees.maxStringLengthErrorMsg)
                    .test({
                        name: 'valid-amount',
                        message: AmountForRegistrationFeeForAttendees.requiredErrorMsg,
                        test: (value) => {
                            const num = parseFloat(value);
                            return !isNaN(num) && num > 0;
                        },
                    }),
            otherwise: (schema) => schema.notRequired(),
        })
});

export const SupportingDocumentValidationSchema = yup.object().shape({
    [SupportingDocument.name]: yup.mixed()
        .test({
            message: SupportingDocument.requiredErrorMsg,
            test: (file: any) => {
                return file !== undefined;
            }
        })
        .test({
            message: SupportingDocument.invalidFileTypeErrorMsg,
            test: (file: any, context) => {
                if (file === SKIP_TO_UPLOAD) return true;

                const extension = getFileExtension(file ? file?.name : '');
                if (extension !== undefined) {
                    const isValid = file && ['doc', 'docx', 'pdf', 'txt', 'text', 'xls', 'xlsx', 'jpg', 'jpeg',
                        'png', 'gif', 'ppt', 'pps', 'pptx', 'ppsx'].includes(extension);
                    if (!isValid) context?.createError();

                    return isValid;
                }
                context?.createError();
            }
        })
        .test({
            message: SupportingDocument.invalidFileSizeErrorMsg,
            test: (file: any) => {
                if (file === SKIP_TO_UPLOAD) return true;

                const isValid = file?.size < MAX_FILE_SIZE;
                return isValid;
            }
        })
});

export const CalendarOfActivitiesValidationSchemas = yup.object().shape({
    [EventPublishedOnRcsiCalendarOfActivities.name]: yup.string().required(EventPublishedOnRcsiCalendarOfActivities.requiredErrorMsg),
    [EventWebsiteUrl.name]: yup.string().max(EventWebsiteUrl.maxStringCount, EventWebsiteUrl.maxStringLengthErrorMsg).trim()
        .test({
            name: 'allowed-prefix',
            message: EventWebsiteUrl.typeError,
            test(value, ctx) {
                if (!value) return true;
                const input = String((ctx?.originalValue ?? value)).trim();
                return input.startsWith('https://') || input.startsWith('www.');
            },
        }).transform((_, orig) => {
            if (orig === null) return "";
            if (typeof orig === 'string') {
                const url = orig.trim();
                if (url.startsWith('www.')) return `https://${url}`;
            }
            return orig;
        }).url(EventWebsiteUrl.typeError),
    [EmailAddress.name]: yup.string()
        .when(EventPublishedOnRcsiCalendarOfActivities.name, {
            is: (val: string) => val.toUpperCase() === YesNoEnum.YES,
            then: s => s.required(EmailAddress.requiredErrorMsg)
                .max(EmailAddress.maxStringCount, EmailAddress.maxStringLengthErrorMsg),
            otherwise: s => s.notRequired()
        }).matches(EMAIL_EXPRESSION, EmailAddress.typeError),
    [ApprovalToAnotherPostgraduateMedicalTrainingBody.name]: yup.string().required(ApprovalToAnotherPostgraduateMedicalTrainingBody.requiredErrorMsg)
});
